import { useRef, useEffect, useState } from "react";

const MU_0 = 4 * Math.PI * 1e-7;

function champDipole2D(x, y, dipole) {
  const dx = x - dipole.x;
  const dy = y - dipole.y;
  const r2 = dx * dx + dy * dy;
  const r = Math.sqrt(r2);
  if (r < 1e-3) return { Bx: 0, By: 0 };

  const factor = (MU_0 * dipole.m) / (4 * Math.PI * Math.pow(r, 5));
  return {
    Bx: factor * (3 * dx * dy),
    By: factor * (3 * dy * dy - r2),
  };
}

function champTotal(x, y, dipoles) {
  return dipoles.reduce(
    (acc, d) => {
      const B = champDipole2D(x, y, d);
      acc.Bx += B.Bx;
      acc.By += B.By;
      return acc;
    },
    { Bx: 0, By: 0 }
  );
}

/* ===== LIGNES ===== */

function traceLigneChamp(x0, y0, dipoles, step = 0.01, n = 900) {
  const pts = [];
  let x = x0;
  let y = y0;

  for (let i = 0; i < n; i++) {
    const { Bx, By } = champTotal(x, y, dipoles);
    const norm = Math.hypot(Bx, By);
    if (norm === 0) break;

    x += (Bx / norm) * step;
    y += (By / norm) * step;
    pts.push({ x, y });

    if (Math.hypot(x, y) > 300) break;
  }
  return pts;
}

/* ===== COULEUR ===== */

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function couleurChamp(norm) {
  const t = clamp(Math.log10(norm + 1e-12) + 10, 0, 6) / 6;
  const r = Math.floor(255 * t);
  const g = Math.floor(255 * (1 - Math.abs(t - 0.5) * 2));
  const b = Math.floor(255 * (1 - t));
  return `rgba(${r},${g},${b},0.8)`;
}

/* ===== COMPOSANT ===== */

export default function ChampMagnetiqueFullScreenClean() {
  const canvasRef = useRef(null);
  const requestRef = useRef(null);
  const particulesRef = useRef([]);
  const lignesRef = useRef([]);

  const NB_PARTICULES = 10000;
  const [mouse, setMouse] = useState({ x: 0.6, y: 0 });
  const [scale, setScale] = useState(200);
  const [showingLines, setShowingLines] = useState(false);

  const dipoles = [
    { x: 0, y: 0, m: 1 },
    { x: mouse.x, y: mouse.y, m: 1 },
  ];

  function creerParticule() {
    const a = Math.random() * 2 * Math.PI;
    const r = 0.25 + Math.random() * 0.1;
    return { x: r * Math.cos(a), y: r * Math.sin(a) };
  }

  // init particules
  useEffect(() => {
    particulesRef.current = Array.from(
      { length: NB_PARTICULES },
      creerParticule
    );
  }, []);

  // resize plein écran
  useEffect(() => {
    function resize() {
      const canvas = canvasRef.current;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      setScale(Math.min(canvas.width, canvas.height) / 4);
    }
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // lignes
  useEffect(() => {
    const seeds = [];
    const r0 = 0.25;
    for (let a = 0; a < 2 * Math.PI; a += Math.PI / 16) {
      seeds.push({ x: r0 * Math.cos(a), y: r0 * Math.sin(a) });
    }

    lignesRef.current = seeds.map(s =>
      traceLigneChamp(s.x, s.y, dipoles)
    );
  }, [mouse]);

  // animation
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    function animate() {
      ctx.fillStyle = "rgba(255,255,255,0.08)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);

      if(showingLines){
        // lignes
        ctx.strokeStyle = "rgba(0,0,0,0.25)";
        ctx.lineWidth = 1;
        lignesRef.current.forEach(ligne => {
          ctx.beginPath();
          ligne.forEach((p, i) => {
            const X = p.x * scale;
            const Y = -p.y * scale;
            if (i === 0) ctx.moveTo(X, Y);
            else ctx.lineTo(X, Y);
          });
          ctx.stroke();
        });
      }
      else{
        // particules
        particulesRef.current.forEach(p => {
          const { Bx, By } = champTotal(p.x, p.y, dipoles);
          const norm = Math.hypot(Bx, By);
          if (norm === 0) return;

          const speed = 0.015 + 0.01 * Math.tanh(norm * 1e6);
          p.x += (Bx / norm) * speed;
          p.y += (By / norm) * speed;

          if (Math.hypot(p.x, p.y) > 300) {
            Object.assign(p, creerParticule());
            return;
          }

          ctx.fillStyle = couleurChamp(norm);
          ctx.fillRect(p.x * scale, -p.y * scale, 3, 3);
        });
      }

      ctx.restore();
      requestRef.current = requestAnimationFrame(animate);
    }

    animate();
    return () => cancelAnimationFrame(requestRef.current);
  }, [mouse, scale, showingLines]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function handleMouseMoveCanvas(e) {
      const rect = canvas.getBoundingClientRect();
      const xCanvas = e.clientX - rect.left;
      const yCanvas = e.clientY - rect.top;

      const x = (xCanvas - rect.width / 2) / scale;
      const y = -(yCanvas - rect.height / 2) / scale;

      setMouse({ x, y });
    }

    window.addEventListener("mousemove", handleMouseMoveCanvas);

    return () => window.removeEventListener("mousemove", handleMouseMoveCanvas);
  }, [scale]);

  useEffect(() => {
    function handleClickCanvas(e) { setShowingLines(s => !s); }

    window.addEventListener("click", handleClickCanvas);
    return () => window.removeEventListener("click", handleClickCanvas);
  }, [setShowingLines]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        pointerEvents: "auto",
        inset: 0,
        width: "100vw",
        height: "100vh",
        display: "block",
        cursor: "crosshair",
      }}
    />
  );
}
